/**
 * Planner (Module A, P1) — manage Resources and Services for the selected org.
 * Resources/Services live in planner_{slug}; their classification comes from
 * the "planning" metadata trees (taxonomies). The Resource×Time calendar is P2.
 *
 * Visibility: sidebar gates by planner:read; this page also shows a notice if
 * the org has the planner feature disabled (the API returns 403).
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarDaysIcon, PlusIcon, TrashIcon, PencilSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import PresenceSubview from '@/components/planner/PresenceSubview';
import PresenceTab from '@/components/planner/PresenceTab';
import SchedulesTab from '@/components/planner/SchedulesTab';
import AvailabilityTab from '@/components/planner/AvailabilityTab';
import { LoadingSpinner } from '@/components/common';
import {
  plannerApi,
  type PlannerResource,
  type PlannerService,
  type PlannerActivity,
  type PlannerTaxonomies,
  type PlannerTaxTree,
} from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import PlanningContainer from '@/components/planner/PlanningContainer';

type TabId = 'planning' | 'presencia' | 'horarios' | 'disponibilidad' | 'recursos' | 'servicios' | 'actividades';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function treeById(tax: PlannerTaxonomies | null, id: string): PlannerTaxTree | undefined {
  return tax?.trees.find((t) => t.id === id);
}
function nodeName(tax: PlannerTaxonomies | null, treeId: string, nodeId?: string | null): string {
  if (!nodeId) return '—';
  const n = treeById(tax, treeId)?.nodes.find((x) => x._id === nodeId);
  return n?.name || '—';
}

export default function Planner() {
  const [activeTab, setActiveTab] = useState<TabId>('planning');
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.PLANNER_MANAGE));

  const [tax, setTax] = useState<PlannerTaxonomies | null>(null);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [loadingTax, setLoadingTax] = useState(true);

  useEffect(() => {
    if (!selectedOrgId) return;
    setLoadingTax(true);
    setTaxError(null);
    plannerApi
      .getTaxonomies()
      .then((r) => setTax(r.data?.data || null))
      .catch((e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        setTaxError(status === 403 ? 'disabled' : 'error');
      })
      .finally(() => setLoadingTax(false));
  }, [selectedOrgId]);

  if (!selectedOrgId) {
    return <div className="p-8 text-center text-gray-500">Selecciona una organización.</div>;
  }

  if (loadingTax) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (taxError === 'disabled') {
    return (
      <div className="p-8">
        <div className="max-w-xl mx-auto text-center bg-amber-50 border border-amber-200 rounded-xl p-8">
          <CalendarDaysIcon className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900">Planificador no activado</h2>
          <p className="text-sm text-gray-600 mt-2">
            El módulo Planificador no está habilitado para esta organización. Actívalo en la
            configuración de la organización (<code>features.planner</code>).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <CalendarDaysIcon className="h-7 w-7 text-vida-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planificador</h1>
          <p className="text-sm text-gray-500">Recursos y servicios. El calendario Recurso×Tiempo llega en la siguiente fase.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {([
          { id: 'planning', label: 'Planning' },
          { id: 'recursos', label: 'Recursos' },
          { id: 'servicios', label: 'Servicios' },
          { id: 'actividades', label: 'Actividades' },
        ] as { id: TabId; label: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
        {/* Separator + Presencia. Visually set apart because it's a different
            workflow (employee shifts vs. generic resource/service planning). */}
        <span className="w-px self-stretch bg-gray-300 mx-1" />
        <button
          onClick={() => setActiveTab('presencia')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'presencia'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400/70'
          }`}
        >
          Presencia
        </button>
        <button
          onClick={() => setActiveTab('horarios')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'horarios'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400/70'
          }`}
        >
          Horarios
        </button>
        <button
          onClick={() => setActiveTab('disponibilidad')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'disponibilidad'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400/70'
          }`}
        >
          Disponibilidad
        </button>
      </div>

      {activeTab === 'planning' && <PlanningContainer tax={tax} canManage={canManage} />}
      {activeTab === 'presencia' && <PresenceTab tax={tax} canManage={canManage} />}
      {activeTab === 'horarios' && <SchedulesTab canManage={canManage} />}
      {activeTab === 'disponibilidad' && <AvailabilityTab canManage={canManage} />}
      {activeTab === 'recursos' && <ResourcesTab tax={tax} canManage={canManage} />}
      {activeTab === 'servicios' && <ServicesTab tax={tax} canManage={canManage} />}
      {activeTab === 'actividades' && <ActivitiesTab canManage={canManage} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

function ResourcesTab({ tax, canManage }: { tax: PlannerTaxonomies | null; canManage: boolean }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [items, setItems] = useState<PlannerResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PlannerResource | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [presenceFor, setPresenceFor] = useState<PlannerResource | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await plannerApi.getResources();
      setItems(r.data?.data || []);
    } catch {
      toast.error('Error al cargar recursos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [selectedOrgId]);

  const remove = async (id: string) => {
    try {
      await plannerApi.deleteResource(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">Recursos</h2>
        {canManage && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
          >
            <PlusIcon className="h-4 w-4" /> Nuevo recurso
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No hay recursos todavía.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium">Tipo</th>
                <th className="text-left px-4 py-2 font-medium">Área</th>
                <th className="text-left px-4 py-2 font-medium">Estado</th>
                <th className="text-right px-4 py-2 font-medium">Cap.</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((r) => (
                <tr key={r._id} className={r.active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-2 text-gray-600">{nodeName(tax, 'resource-types', r.typeNodeId)}</td>
                  <td className="px-4 py-2 text-gray-600">{nodeName(tax, 'business-areas', r.businessAreaNodeId)}</td>
                  <td className="px-4 py-2 text-gray-600">{nodeName(tax, 'resource-states', r.currentStateNodeId)}</td>
                  <td className="px-4 py-2 text-right">{r.capacity}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {r.employeeNif && (
                      <button onClick={() => setPresenceFor(r)} title="Control de presencia"
                        className="p-1.5 text-gray-500 hover:text-vida-600"><ClockIcon className="h-4 w-4" /></button>
                    )}
                    {canManage && (
                      <>
                        <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 text-gray-500 hover:text-gray-900"><PencilSquareIcon className="h-4 w-4" /></button>
                        <button onClick={() => remove(r._id)} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ResourceForm
          tax={tax}
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); void load(); }}
        />
      )}

      {presenceFor && (
        <PresenceSubview resource={presenceFor} onClose={() => setPresenceFor(null)} />
      )}
    </div>
  );
}

function ResourceForm({ tax, initial, onClose, onSaved }: {
  tax: PlannerTaxonomies | null; initial: PlannerResource | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [typeNodeId, setTypeNodeId] = useState(initial?.typeNodeId || '');
  const [businessAreaNodeId, setBusinessAreaNodeId] = useState(initial?.businessAreaNodeId || '');
  const [currentStateNodeId, setCurrentStateNodeId] = useState(initial?.currentStateNodeId || '');
  const [capacity, setCapacity] = useState(initial?.capacity ?? 1);
  const [exclusive, setExclusive] = useState(initial?.exclusive ?? true);
  const [active, setActive] = useState(initial?.active ?? true);
  const [employeeNif, setEmployeeNif] = useState(initial?.employeeNif || '');
  const [scheduleId, setScheduleId] = useState(initial?.scheduleId || '');
  const [schedules, setSchedules] = useState<{ _id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const types = treeById(tax, 'resource-types')?.nodes || [];
  const areas = treeById(tax, 'business-areas')?.nodes || [];
  const states = treeById(tax, 'resource-states')?.nodes || [];

  useEffect(() => {
    plannerApi.listSchedules()
      .then((r) => setSchedules(((r.data.data || []) as { _id: string; name: string }[])))
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      typeNodeId: typeNodeId || null,
      businessAreaNodeId: businessAreaNodeId || null,
      currentStateNodeId: currentStateNodeId || null,
      capacity: Number(capacity) || 1,
      exclusive,
      active,
      employeeNif: employeeNif.trim() || null,
      scheduleId: scheduleId || null,
    };
    try {
      if (initial) await plannerApi.updateResource(initial._id, payload);
      else await plannerApi.createResource(payload);
      toast.success('Recurso guardado');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial ? 'Editar recurso' : 'Nuevo recurso'} onClose={onClose} onSave={save} saving={saving}>
      <div>
        <label className={labelClass}>Nombre *</label>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Baño 1.1" />
      </div>
      <SelectNode label="Tipo" value={typeNodeId} onChange={setTypeNodeId} nodes={types} />
      <SelectNode label="Área de negocio" value={businessAreaNodeId} onChange={setBusinessAreaNodeId} nodes={areas} />
      <SelectNode label="Estado" value={currentStateNodeId} onChange={setCurrentStateNodeId} nodes={states} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Capacidad</label>
          <input type="number" min={1} className={inputClass} value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} />
        </div>
        <div className="flex items-end gap-4 pb-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={exclusive} onChange={(e) => setExclusive(e.target.checked)} /> Exclusivo</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Activo</label>
        </div>
      </div>
      <div>
        <label className={labelClass}>NIF de empleado (control de presencia)</label>
        <input className={inputClass} value={employeeNif} onChange={(e) => setEmployeeNif(e.target.value)} placeholder="Opcional — solo si el recurso es una persona vinculada a gk2control" />
        <p className="text-xs text-gray-400 mt-1">Vincula los fichajes externos con este recurso. Si no usas control de presencia, déjalo vacío.</p>
      </div>
      <div>
        <label className={labelClass}>Horario oficial</label>
        <select className={inputClass} value={scheduleId} onChange={(e) => setScheduleId(e.target.value)}>
          <option value="">— sin horario</option>
          {schedules.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <p className="text-xs text-gray-400 mt-1">Si lo asignas, la salida forzosa del auto-fichaje sale de este horario. Gestiona los horarios en la pestaña «Horarios».</p>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

function ServicesTab({ tax, canManage }: { tax: PlannerTaxonomies | null; canManage: boolean }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [items, setItems] = useState<PlannerService[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PlannerService | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await plannerApi.getServices();
      setItems(r.data?.data || []);
    } catch {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [selectedOrgId]);

  const remove = async (id: string) => {
    try {
      await plannerApi.deleteService(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">Servicios</h2>
        {canManage && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
          >
            <PlusIcon className="h-4 w-4" /> Nuevo servicio
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No hay servicios todavía.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium">Categoría</th>
                <th className="text-right px-4 py-2 font-medium">Duración</th>
                <th className="text-right px-4 py-2 font-medium">Precio</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((s) => (
                <tr key={s._id} className={s.active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-2 text-gray-600">{nodeName(tax, 'service-categories', s.categoryNodeId)}</td>
                  <td className="px-4 py-2 text-right">{s.duration} min</td>
                  <td className="px-4 py-2 text-right">{s.price != null ? `${s.price.toFixed(2)} ${s.currency}` : '—'}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {canManage && (
                      <>
                        <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-1.5 text-gray-500 hover:text-gray-900"><PencilSquareIcon className="h-4 w-4" /></button>
                        <button onClick={() => remove(s._id)} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ServiceForm
          tax={tax}
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); void load(); }}
        />
      )}
    </div>
  );
}

function ServiceForm({ tax, initial, onClose, onSaved }: {
  tax: PlannerTaxonomies | null; initial: PlannerService | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [categoryNodeId, setCategoryNodeId] = useState(initial?.categoryNodeId || '');
  const [duration, setDuration] = useState(initial?.duration ?? 30);
  const [price, setPrice] = useState<string>(initial?.price != null ? String(initial.price) : '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);

  const categories = treeById(tax, 'service-categories')?.nodes || [];

  const save = async () => {
    if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      categoryNodeId: categoryNodeId || null,
      duration: Number(duration) || 30,
      price: price.trim() === '' ? null : Number(price),
      active,
    };
    try {
      if (initial) await plannerApi.updateService(initial._id, payload);
      else await plannerApi.createService(payload);
      toast.success('Servicio guardado');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial ? 'Editar servicio' : 'Nuevo servicio'} onClose={onClose} onSave={save} saving={saving}>
      <div>
        <label className={labelClass}>Nombre *</label>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Baños termales" />
      </div>
      <SelectNode label="Categoría" value={categoryNodeId} onChange={setCategoryNodeId} nodes={categories} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Duración (min) *</label>
          <input type="number" min={1} className={inputClass} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
        </div>
        <div>
          <label className={labelClass}>Precio (opcional)</label>
          <input type="number" min={0} step="0.01" className={inputClass} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="—" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Activo</label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

function ActivitiesTab({ canManage }: { canManage: boolean }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [items, setItems] = useState<PlannerActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PlannerActivity | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await plannerApi.getActivities();
      setItems(r.data?.data || []);
    } catch {
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [selectedOrgId]);

  const remove = async (id: string) => {
    try {
      await plannerApi.deleteActivity(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">Actividades</h2>
        {canManage && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
            <PlusIcon className="h-4 w-4" /> Nueva actividad
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No hay actividades todavía. Son opcionales: agrupan la planificación por ámbito.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium">Formato</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((a) => (
                <tr key={a._id} className={a.active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 font-medium text-gray-900">{a.name}</td>
                  <td className="px-4 py-2 text-gray-600">{a.format}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {canManage && (
                      <>
                        <button onClick={() => { setEditing(a); setShowForm(true); }} className="p-1.5 text-gray-500 hover:text-gray-900"><PencilSquareIcon className="h-4 w-4" /></button>
                        <button onClick={() => remove(a._id)} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ActivityForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); void load(); }} />
      )}
    </div>
  );
}

function ActivityForm({ initial, onClose, onSaved }: { initial: PlannerActivity | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name || '');
  const [format, setFormat] = useState<PlannerActivity['format']>(initial?.format || 'calendar');
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (initial) await plannerApi.updateActivity(initial._id, { name: name.trim(), format, active });
      else await plannerApi.createActivity({ name: name.trim(), format, active });
      toast.success('Actividad guardada');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial ? 'Editar actividad' : 'Nueva actividad'} onClose={onClose} onSave={save} saving={saving}>
      <div>
        <label className={labelClass}>Nombre *</label>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Consultas, Mantenimiento…" />
      </div>
      <div>
        <label className={labelClass}>Formato</label>
        <select className={inputClass} value={format} onChange={(e) => setFormat(e.target.value as PlannerActivity['format'])}>
          <option value="calendar">Calendario</option>
          <option value="agenda">Agenda</option>
          <option value="list">Lista</option>
          <option value="cmi">CMI</option>
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Activa</label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function SelectNode({ label, value, onChange, nodes }: {
  label: string; value: string; onChange: (v: string) => void; nodes: { _id: string; name: string; depth: number }[];
}) {
  const options = useMemo(() => nodes, [nodes]);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((n) => (
          <option key={n._id} value={n._id}>{' '.repeat(n.depth * 2)}{n.name}</option>
        ))}
      </select>
    </div>
  );
}

function Modal({ title, children, onClose, onSave, saving }: {
  title: string; children: React.ReactNode; onClose: () => void; onSave: () => void; saving: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <div className="space-y-4">{children}</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50">Cancelar</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
            {saving && <LoadingSpinner size="sm" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
